'use client';

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * Compact lifecycle badge for a search result row.
 *
 * Renders nothing for `active`, which is ~92% of the register — a badge on
 * every row would be noise and would drown the ~8% that actually carry a
 * signal.
 *
 * Colours follow severity rather than the palette order: insolvency and
 * deletion are red, liquidation (a process still running) is orange, a
 * finished dissolution is amber.
 */
const STATUS_STYLES: Record<string, string> = {
  insolvent:
    'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400',
  deleted:
    'bg-destructive/10 border-destructive/30 text-destructive',
  in_liquidation:
    'bg-orange-500/10 border-orange-500/30 text-orange-600 dark:text-orange-400',
  dissolved:
    'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400',
};

/** i18n keys under `search.sidebar`, shared with the filter so the badge and
 *  the checkbox that finds it always read the same. */
const STATUS_LABEL_KEYS: Record<string, { key: string; fallback: string }> = {
  insolvent: { key: 'legalStatusOptions.insolvent', fallback: 'Insolvent' },
  in_liquidation: {
    key: 'legalStatusOptions.inLiquidation',
    fallback: 'In liquidation',
  },
  dissolved: { key: 'legalStatusOptions.dissolved', fallback: 'Dissolved' },
  deleted: { key: 'legalStatusOptions.deleted', fallback: 'Deleted' },
};

export function LegalStatusBadge({
  status,
  className,
}: {
  status?: string | null;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!status || status === 'active') return null;

  const label = STATUS_LABEL_KEYS[status];
  if (!label) return null;

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border px-1.5 py-px',
        'text-[10px] font-medium leading-none whitespace-nowrap',
        STATUS_STYLES[status],
        className,
      )}
    >
      {t(`search.sidebar.${label.key}`, { defaultValue: label.fallback })}
    </span>
  );
}
